import {EditOutlined, RollbackOutlined} from '@ant-design/icons';
import {Button, Col, Form, Row, Skeleton, message} from 'antd';
import {BlockList} from 'components/shop/BlockList';
import {FormShop} from 'components/shop/FormShop';
import {
  ShopWeeklyScheduleManager,
  DayScheduleData,
  dayjsToMinutes,
} from 'components/shop/ShopWeeklyScheduleManager';
import dayjs from 'dayjs';
import {useGetShopById} from 'hooks/shop/useGetShop';
import {useUpdateShop} from 'hooks/shop/useUpdateShop';
import React, {useEffect, useState, useTransition} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import Api from 'configs';

const format = 'HH:mm';

export const EditShop: React.FC = () => {
  const params = useParams();
  const {
    data,
    isLoading: isLoadingShop,
    isError,
    isSuccess,
    refetch,
  } = useGetShopById(params?.id || '');
  const navigate = useNavigate();
  const [_, startTransition] = useTransition();
  const [form] = Form.useForm();
  const {mutateAsync: onUpdateShop, isLoading} = useUpdateShop();

  // State to track form dirty status
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Weekly hours, timezone, always-open mode, and bell status
  const [weeklyHours, setWeeklyHours] = useState<DayScheduleData[]>([]);
  const [timezone, setTimezone] = useState<string>('Asia/Ho_Chi_Minh');
  const [isAlwaysOpen, setIsAlwaysOpen] = useState<boolean>(false);
  const [shopBellEnabled, setShopBellEnabled] = useState<boolean>(true);

  useEffect(() => {
    form.resetFields();
    setIsFormDirty(false);
    if (isSuccess && data) {
      form.setFieldsValue({
        ...data,
        numberPhone: data?.user?.numberPhone || data?.numberPhone || '',
        openingHour: data?.openingHour ? dayjs(data.openingHour, format) : undefined,
        closingHour: data?.closingHour ? dayjs(data.closingHour, format) : undefined,
        isAlwaysOpen: data?.isAlwaysOpen === true,
      });

      setTimezone(data?.timezone || 'Asia/Ho_Chi_Minh');
      setIsAlwaysOpen(data?.isAlwaysOpen === true);
      setShopBellEnabled(data?.shopBellEnabled !== false);

      if (data?.weeklyHours && Array.isArray(data.weeklyHours) && data.weeklyHours.length > 0) {
        setWeeklyHours(data.weeklyHours);
      } else {
        // Initialize 7 days
        // Check if shop name or opening/closing hours indicate specific hours
        let defaultStart = 480;  // 08:00
        let defaultEnd = 1320;   // 22:00

        // If shop name contains 5h30 - 13h, auto-detect for convenience
        if (data?.name && (data.name.includes('5h30') || data.name.includes('5:30'))) {
          defaultStart = 330; // 05:30
          defaultEnd = 780;   // 13:00
        } else if (data?.openingHour && data?.closingHour) {
          try {
            const startD = dayjs(data.openingHour, format);
            const endD = dayjs(data.closingHour, format);
            if (startD.isValid() && endD.isValid()) {
              defaultStart = dayjsToMinutes(startD);
              defaultEnd = dayjsToMinutes(endD);
            }
          } catch {
            // keep default
          }
        }

        const initialWeekly: DayScheduleData[] = [];
        for (let d = 0; d <= 6; d++) {
          initialWeekly.push({
            dayOfWeek: d,
            slots: [
              {start: defaultStart, end: defaultEnd, isOpen: true},
            ],
          });
        }
        setWeeklyHours(initialWeekly);
      }
    }
    if (isError) {
      message.error(`Không tìm thấy shop có id: ${params?.id}`);
      navigate('/shop', {
        state: {from: `/shop/edit/${params?.id}`},
      });
    }
  }, [isSuccess, data]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Validate weekly schedule slots if not always open
      for (const day of weeklyHours) {
        if (day.slots && day.slots.length > 0) {
          for (let i = 0; i < day.slots.length; i++) {
            const slot = day.slots[i];
            if (slot.start >= slot.end) {
              message.error(`Khung giờ ${i + 1} (thứ ${day.dayOfWeek}): Giờ mở cửa phải trước giờ đóng cửa!`);
              return;
            }
          }
        }
      }

      // Determine target user/shop id
      const targetShopId = data?._id || params?.id || '';

      await onUpdateShop({
        idShop: targetShopId,
        body: {
          ...values,
          openingHour: values?.openingHour ? (typeof values.openingHour === 'string' ? values.openingHour : values.openingHour.format(format)) : data?.openingHour,
          closingHour: values?.closingHour ? (typeof values.closingHour === 'string' ? values.closingHour : values.closingHour.format(format)) : data?.closingHour,
          isAlwaysOpen,
          timezone,
          weeklyHours,
          shopBellEnabled,
        },
        idQuery: targetShopId,
      });

      // Synchronize weekly schedule directly with dedicated endpoint
      if (targetShopId) {
        try {
          await Api.put(`/shops/${targetShopId}/weekly-schedule`, {
            timezone,
            weeklyHours,
          });
          await Api.patch(`/shops/${targetShopId}/bell`, {
            enabled: shopBellEnabled,
          });
        } catch (e) {
          console.log('Error calling weekly-schedule endpoint:', e);
        }
      }

      message.success('Cập nhật thông tin và lịch hoạt động shop thành công');
      setIsFormDirty(false);
      refetch();
    } catch (error: any) {
      if (error?.data?.message) {
        message.error(error?.data?.message);
      } else if (error?.response?.data?.message) {
        message.error(error?.response?.data?.message);
      } else if (error?.message) {
        message.error(error?.message);
      }
    }
  };

  return (
    <div className="mx-auto container pb-8">
      <div className="flex-col gap-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="font-bold text-2xl text-gray-900">Chỉnh sửa thông tin Shop</h2>
            {data?.name && (
              <p className="text-gray-500 text-sm mt-0.5">
                {data.name} &bull; ID: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{params?.id}</code>
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              icon={<RollbackOutlined />}
              onClick={() => {
                navigate(-1);
              }}>
              Trở về
            </Button>
            <Button
              loading={isLoading}
              className="bg-[#eca52b] hover:!bg-[#edb555] !border-white !font-medium !text-white"
              icon={<EditOutlined />}
              onClick={() => {
                startTransition(() => {
                  handleSave();
                });
              }}>
              Lưu thay đổi
            </Button>
          </div>
        </div>

        <Skeleton active loading={isLoadingShop}>
          {/* Form thông tin chung của Shop */}
          <FormShop
            form={form}
            isEdit={true}
            onValuesChange={() => {
              if (!isFormDirty) {
                setIsFormDirty(true);
              }
            }}
          />

          {/* Quản lý thời gian, lịch mở cửa hàng tuần */}
          <div className="my-6">
            <ShopWeeklyScheduleManager
              weeklyHours={weeklyHours}
              timezone={timezone}
              isAlwaysOpen={isAlwaysOpen}
              shopBellEnabled={shopBellEnabled}
              isOpenStatus={data?.isOpen}
              onChangeAlwaysOpen={val => {
                setIsAlwaysOpen(val);
                form.setFieldsValue({isAlwaysOpen: val});
                setIsFormDirty(true);
              }}
              onChangeWeeklyHours={hours => {
                setWeeklyHours(hours);
                setIsFormDirty(true);
              }}
              onChangeShopBell={bell => {
                setShopBellEnabled(bell);
                setIsFormDirty(true);
              }}
            />
          </div>

          <Row>
            <Col span={24}>
              <h3 className="font-bold text-xl mb-3">Danh sách tài xế bị chặn</h3>
            </Col>
            <Col span={24}>
              <BlockList shopId={params?.id || ''} data={data?.blockedlist} />
            </Col>
          </Row>
        </Skeleton>
      </div>
    </div>
  );
};
